import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, ShieldAlert } from "lucide-react";

// Session Z52 — Weekly rate-limit offenders for cleaning companies.
export default function RateLimitWeeklyCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/cleaning-companies/rate-limit-weekly")
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ items: [], count: 0 }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const items = data?.items || [];

  return (
    <div className="ns-card p-6" data-testid="rate-limit-weekly-card">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="ns-label">Supplier health · 7 days</div>
          <div className="font-serif text-xl mt-1">Retry rate-limit hits</div>
          <div className="text-xs text-slate-400 mt-1">
            Companies that bumped against the 3-retries-per-hour ceiling this week. Persistent hits mean a stuck webhook or a wrong recipient — fix the supplier before the next blast.
          </div>
        </div>
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
          items.length === 0 ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"
        }`}>
          {items.length === 0
            ? <ShieldAlert className="w-4 h-4" />
            : <AlertTriangle className="w-4 h-4" />}
        </div>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="text-xs text-slate-400 italic py-4">Loading…</div>
        ) : items.length === 0 ? (
          <div
            data-testid="rate-limit-weekly-empty"
            className="text-xs text-slate-500 py-3 bg-emerald-50/40 border border-emerald-100 rounded-lg px-3"
          >
            All clear — no company hit the retry ceiling in the last 7 days.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="rate-limit-weekly-rows">
            {items.map((it) => (
              <li
                key={it.company_id}
                data-testid={`rate-limit-row-${it.company_id}`}
                className="py-2 flex items-center justify-between gap-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {it.name || "(unknown)"}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span>
                      last hit{" "}
                      {it.last_hit_at
                        ? new Date(it.last_hit_at).toLocaleString("en-GB")
                        : "—"}
                    </span>
                    {Object.entries(it.channels || {}).map(([ch, n]) => (
                      <span
                        key={ch}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-stone-50 px-2 py-0.5"
                      >
                        {ch} · {n}
                      </span>
                    ))}
                  </div>
                </div>
                <span className={`text-[11px] tracking-widest uppercase rounded-full px-2.5 py-1 ${
                  it.hits >= 3
                    ? "bg-red-50 text-red-700 border border-red-100"
                    : "bg-amber-50 text-amber-700 border border-amber-100"
                }`}>
                  {it.hits} hit{it.hits === 1 ? "" : "s"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
